import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    Stepper,
    Step,
    StepLabel,
    Paper,
    LinearProgress,
    MenuItem,
    Select,
    FormControl,
    IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TerrainIcon from '@mui/icons-material/Terrain';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import AirIcon from '@mui/icons-material/Air';
import CloudIcon from '@mui/icons-material/Cloud';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ScienceIcon from '@mui/icons-material/Science';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { LanguageContext } from '../App';
import { getText } from '../utils/translations';

const SugarcaneDashboard = () => {
    const { farmerId } = useParams();
    const navigate = useNavigate();
    const { language } = useContext(LanguageContext);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [error, setError] = useState(null);
    const [updateForm, setUpdateForm] = useState({
        planting_date: '',
        ratoon_number: 0,
        sugarcane_variety: 'Co 0238',
        irrigation_method: 'Flood',
        latitude: '',
        longitude: '',
        soil_type: 'Loamy'
    });

    const detectLocation = async () => {
        setLoading(true);
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUpdateForm(prev => ({
                        ...prev,
                        latitude: position.coords.latitude.toFixed(6),
                        longitude: position.coords.longitude.toFixed(6)
                    }));
                    setLoading(false);
                },
                async (err) => {
                    console.warn("Browser geolocation failed, trying IP-based fallback...", err);
                    // Use a toast or inline message instead of a blocking alert if possible
                    await fetchIpLocation();
                },
                { timeout: 8000, enableHighAccuracy: true }
            );
        } else {
            await fetchIpLocation();
        }
    };

    const fetchIpLocation = async () => {
        try {
            // Using a more reliable public API
            const res = await axios.get('https://ipapi.co/json/').catch(() => axios.get('https://ip-api.com/json/'));
            if (res.data && (res.data.latitude || res.data.lat)) {
                setUpdateForm(prev => ({
                    ...prev,
                    latitude: (res.data.latitude || res.data.lat).toFixed(6),
                    longitude: (res.data.longitude || res.data.lon).toFixed(6)
                }));
            } else {
                alert("Auto-detection unavailable. Please enter coordinates from your location app or Google Maps.");
            }
        } catch (err) {
            console.error("IP fallback failed:", err);
            alert("Location detection failed. Please manually enter your field's Latitude/Longitude.");
        } finally {
            setLoading(false);
        }
    };

    const SUGARCANE_VARIETIES = [
        { id: 'Co 0238', name: 'Co 0238 (Early)' },
        { id: 'CoS 13235', name: 'CoS 13235 (Mid-late)' },
        { id: 'CoLk 14201', name: 'CoLk 14201 (Mid-late)' },
        { id: 'CoLk 15201', name: 'CoLk 15201 (Mid-late)' },
        { id: 'CoLk 16202', name: 'CoLk 16202 (Mid-late)' },
        { id: 'CoSe 17451', name: 'CoSe 17451 (East UP)' },
        { id: 'CoLk 15466', name: 'CoLk 15466 (East UP)' },
        { id: 'Co 15023', name: 'Co 15023 (Early)' },
        { id: 'Co 0118', name: 'Co 0118 (Early)' },
        { id: 'CoS 19231', name: 'CoS 19231 (Early)' }
    ];

    useEffect(() => {
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [farmerId]);

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`/api/sugarcane/dashboard/${farmerId}`);
            if (res.data.status === 'setup_required') {
                setError('Setup required');
            } else {
                setDashboardData(res.data);
                if (res.data.farmer_info) {
                    setUpdateForm({
                        planting_date: res.data.farmer_info.planting_date || '',
                        ratoon_number: res.data.farmer_info.ratoon_number || 0,
                        sugarcane_variety: res.data.farmer_info.variety || 'Co 0238',
                        irrigation_method: res.data.farmer_info.irrigation_method || 'Flood',
                        latitude: res.data.farmer_info.lat || '',
                        longitude: res.data.farmer_info.lon || '',
                        soil_type: res.data.farmer_info.soil_type || 'Loamy'
                    });
                }
                setError(null);
            }
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load sugarcane data');
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        try {
            await axios.post(`/api/sugarcane/update-management/${farmerId}`, updateForm);
            fetchDashboard();
            setError(null);
        } catch (err) {
            alert('Failed to update management profile');
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
            <CircularProgress sx={{ color: '#00BCD4' }} />
        </Box>
    );

    // Common Select Menu Props for Premium Theme
    const selectMenuProps = {
        PaperProps: {
            sx: {
                bgcolor: '#050a05',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
                '& .MuiMenuItem-root': {
                    color: '#b0bec5',
                    '&:hover': {
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        color: '#4CAF50'
                    },
                    '&.Mui-selected': {
                        bgcolor: 'rgba(76, 175, 80, 0.2)',
                        color: '#4CAF50',
                        fontWeight: 'bold',
                        '&:hover': {
                            bgcolor: 'rgba(76, 175, 80, 0.3)'
                        }
                    }
                }
            }
        }
    };

    // If there's an error and it's NOT a setup requirement, show only the error
    if (error && error !== 'Setup required') return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', color: '#f44336' }}>{error}</Alert>
            <Button variant="outlined" color="primary" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
                {getText('back_dashboard', language)}
            </Button>
        </Container>
    );

    // If setup is required OR dashboard data is still null (to prevent destructuring crash)
    if (error === 'Setup required' || !dashboardData) return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: '#00BCD4' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="body1" sx={{ color: '#00BCD4', cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate(-1)}>
                        {getText('back_dashboard', language)}
                    </Typography>
                </Box>
                <Paper className="premium-card" sx={{ p: 5, textAlign: 'center', border: '1px solid rgba(0, 188, 212, 0.3) !important' }}>
                    <Typography variant="h4" className="text-shimmer" sx={{ mb: 2, fontSize: '2.5rem' }}>
                        {getText('setup_title', language)}
                    </Typography>
                    <Typography sx={{ color: '#b0bec5', mb: 5, fontSize: '1.1rem', maxWidth: '80%', mx: 'auto' }}>
                        {getText('setup_desc', language)}
                    </Typography>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <Typography variant="caption" sx={{ color: '#00BCD4', mb: 1, textAlign: 'left', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Planting Date
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="date"
                                    value={updateForm.planting_date}
                                    onChange={(e) => setUpdateForm({ ...updateForm, planting_date: e.target.value })}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            color: '#ffffff',
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            borderRadius: '12px',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 188, 212, 0.3) !important' }
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <Typography variant="caption" sx={{ color: '#00BCD4', mb: 1, textAlign: 'left', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Sugarcane Variety
                                </Typography>
                                <Select
                                    value={updateForm.sugarcane_variety}
                                    onChange={(e) => setUpdateForm({ ...updateForm, sugarcane_variety: e.target.value })}
                                    MenuProps={selectMenuProps}
                                    sx={{
                                        color: '#ffffff',
                                        bgcolor: 'rgba(255,255,255,0.03)',
                                        borderRadius: '12px',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 188, 212, 0.3) !important' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00BCD4 !important' },
                                        textAlign: 'left'
                                    }}
                                >
                                    {SUGARCANE_VARIETIES.map(v => (
                                        <MenuItem key={v.id} value={v.id} sx={{ py: 1.5 }}>{v.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <Typography variant="caption" sx={{ color: '#00BCD4', mb: 1, textAlign: 'left', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Soil Type
                                </Typography>
                                <Select
                                    value={updateForm.soil_type}
                                    onChange={(e) => setUpdateForm({ ...updateForm, soil_type: e.target.value })}
                                    MenuProps={selectMenuProps}
                                    sx={{
                                        color: '#ffffff',
                                        bgcolor: 'rgba(255,255,255,0.03)',
                                        borderRadius: '12px',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 188, 212, 0.3) !important' },
                                        textAlign: 'left'
                                    }}
                                >
                                    <MenuItem value="Sandy">Sandy (10-15%)</MenuItem>
                                    <MenuItem value="Loamy">Loamy (18-25%)</MenuItem>
                                    <MenuItem value="Clay">Clay (25-35%)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <Typography variant="caption" sx={{ color: '#00BCD4', mb: 1, textAlign: 'left', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Latitude
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="e.g. 26.8467"
                                    value={updateForm.latitude}
                                    onChange={(e) => setUpdateForm({ ...updateForm, latitude: e.target.value })}
                                    sx={{
                                        '& .MuiInputBase-root': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '12px' },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 188, 212, 0.3) !important' }
                                    }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <Typography variant="caption" sx={{ color: '#00BCD4', mb: 1, textAlign: 'left', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Longitude
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="e.g. 80.9462"
                                    value={updateForm.longitude}
                                    onChange={(e) => setUpdateForm({ ...updateForm, longitude: e.target.value })}
                                    sx={{
                                        '& .MuiInputBase-root': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '12px' },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 188, 212, 0.3) !important' }
                                    }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                onClick={detectLocation}
                                startIcon={<MyLocationIcon />}
                                sx={{
                                    color: '#00BCD4',
                                    borderColor: 'rgba(0, 188, 212, 0.3)',
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    width: '100%',
                                    '&:hover': { borderColor: '#00BCD4', bgcolor: 'rgba(0, 188, 212, 0.05)' }
                                }}
                            >
                                Detect My Field Location
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleUpdate}
                                    sx={{
                                        background: 'linear-gradient(90deg, #00BCD4 0%, #4CAF50 100%)',
                                        color: 'white',
                                        py: 2,
                                        borderRadius: '12px',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 20px rgba(0, 188, 212, 0.3)',
                                        textTransform: 'none'
                                    }}
                                >
                                    {getText('activate_dss', language)}
                                </Button>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Paper>
            </motion.div>
        </Container >
    );

    // Guaranteed to have dashboardData here
    const { farmer_info, irrigation, fertilizer_schedule } = dashboardData;

    const getPreciseRecommendation = () => {
        if (!irrigation || irrigation.status === 'no_data' || irrigation.recommended_water_mm === 0) return null;

        const reason = getText(`reason_${irrigation.primary_reason}`, language);
        const template = getText('irrigate_template', language);

        return template
            .replace('{volume}', irrigation.recommended_water_mm)
            .replace('{reason}', reason);
    };

    const preciseMsg = getPreciseRecommendation();

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {/* Back Button and Header */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: '#00BCD4', p: 0.5 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" sx={{ color: '#00BCD4', cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate(-1)}>
                        {getText('back_dashboard', language)}
                    </Typography>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 3 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="h3" sx={{ color: '#00BCD4', fontWeight: 'bold', letterSpacing: -1 }}>🎋 {getText('sugarcane_mgmt', language)}</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/crop-quality-test/${farmerId}`)}
                                sx={{
                                    color: '#9C27B0',
                                    borderColor: 'rgba(156, 39, 176, 0.4)',
                                    borderRadius: '8px',
                                    px: 2,
                                    mr: 1,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.05)', borderColor: '#9C27B0' }
                                }}
                                startIcon={<ScienceIcon />}
                            >
                                {getText('crop_quality_test', language)}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setError('Setup required')}
                                sx={{
                                    color: '#00BCD4',
                                    borderColor: 'rgba(0,188,212,0.4)',
                                    borderRadius: '8px',
                                    px: 2,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'rgba(0,188,212,0.05)', borderColor: '#00BCD4' }
                                }}
                            >
                                {getText('change_variety', language)}
                            </Button>
                        </Box>
                        <Typography variant="h6" sx={{ color: '#b0bec5', mb: 1.5, opacity: 0.9 }}>
                            {farmer_info.name} • {farmer_info.district}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                                label={farmer_info.variety}
                                sx={{ bgcolor: '#4CAF50', color: '#fff', fontWeight: 'bold', borderRadius: '8px' }}
                            />
                            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', height: 20, my: 'auto' }} />
                            {dashboardData.variety_traits?.traits?.map((trait, i) => (
                                <Chip
                                    key={i}
                                    size="small"
                                    label={trait}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#b0bec5', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px' }}
                                />
                            ))}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#78909c', mt: 1.5, maxWidth: '600px', fontStyle: 'italic' }}>
                            {dashboardData.variety_traits?.description}
                        </Typography>
                    </Box>
                    <Paper sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'rgba(0, 188, 212, 0.08)',
                        border: '1px solid rgba(0, 188, 212, 0.2)',
                        borderRadius: '16px'
                    }}>
                        <TerrainIcon sx={{ color: '#00BCD4', fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', textTransform: 'uppercase', fontWeight: 'bold' }}>{getText('crop_age', language)}</Typography>
                            <Typography variant="h5" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>{farmer_info.crop_age_days} {getText('days', language)}</Typography>
                        </Box>
                    </Paper>
                </Box>

                <Grid container spacing={3}>
                    {/* LEFT COLUMN: Real-time Status */}
                    <Grid item xs={12} lg={4}>
                        <Card className="premium-card" sx={{ height: '100%', border: '1px solid rgba(0, 188, 212, 0.2) !important' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h5" sx={{ color: '#00BCD4', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
                                    <WaterDropIcon sx={{ color: '#00BCD4' }} /> {getText('irrigation_status', language)}
                                </Typography>

                                {irrigation.status === 'no_data' ? (
                                    /* NO DATA AVAILABLE - Show Alert */
                                    <Alert
                                        severity="warning"
                                        icon={<WarningAmberIcon />}
                                        sx={{
                                            mb: 3,
                                            borderRadius: '12px',
                                            border: '2px solid rgba(255, 152, 0, 0.3)',
                                            backgroundColor: 'rgba(255, 152, 0, 0.1)'
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#fff', mb: 1 }}>
                                            {getText('moisture_data_unavailable', language)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6" sx={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}>
                                                {getText('fert_schedule', language)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Chip
                                                    size="small"
                                                    label={language === 'hi' ? `${farmer_info.expected_yield || 100} टन उपज के लिए` : `For ${farmer_info.expected_yield || 100}t Yield`}
                                                    sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50', border: '1px solid rgba(76, 175, 80, 0.2)', fontSize: '0.65rem', height: '20px' }}
                                                />
                                                <Chip size="small" label="AI Optimized" sx={{ bgcolor: 'rgba(103, 58, 183, 0.1)', color: '#9575cd', border: '1px solid rgba(103, 58, 183, 0.2)', fontSize: '0.65rem', height: '20px' }} />
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                                            {getText('please_enter_soil_data', language)}
                                        </Typography>
                                    </Alert>
                                ) : (
                                    /* NORMAL IRRIGATION DATA */
                                    <>
                                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                                            <Typography variant="body2" sx={{ color: '#b0bec5', mb: 1 }}>{getText('current_moisture', language)}</Typography>
                                            <Typography variant="h2" sx={{
                                                color: irrigation.urgency === 'Critical' ? '#f44336' : (irrigation.urgency === 'Excessive' ? '#03a9f4' : (irrigation.urgency === 'Medium' ? '#ff9800' : '#4CAF50')),
                                                fontWeight: 'bold',
                                                textShadow: irrigation.urgency === 'Critical' ? '0 0 20px rgba(244, 67, 54, 0.3)' : '0 0 20px rgba(76, 175, 80, 0.3)'
                                            }}>
                                                {irrigation.soil_moisture}%
                                            </Typography>
                                            <Box sx={{ mt: 2 }}>
                                                {/* Relative Moisture Bar: Normalizes raw % to a 'Health' scale */}
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(100, (irrigation.soil_moisture / (irrigation.thresholds?.optimal * 1.3)) * 100)}
                                                    sx={{
                                                        height: 14,
                                                        borderRadius: 7,
                                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: irrigation.urgency === 'Critical' ? '#f44336' : (irrigation.urgency === 'Excessive' ? '#03a9f4' : (irrigation.urgency === 'Medium' ? '#ff9800' : '#4CAF50')),
                                                            boxShadow: '0 0 10px currentColor'
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Alert
                                            severity={irrigation.urgency === 'Critical' ? 'error' : (irrigation.urgency === 'Excessive' ? 'info' : (irrigation.urgency === 'Medium' ? 'warning' : 'success'))}
                                            sx={{
                                                mb: 3,
                                                borderRadius: '12px',
                                                border: '1px solid rgba(255,255,255,0.05)',
                                                bgcolor: irrigation.urgency === 'Low' ? 'rgba(76, 175, 80, 0.1)' : 'auto',
                                                '& .MuiAlert-message': { width: '100%' }
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                                {getText('urgency', language).toUpperCase()}: {irrigation.urgency === 'Excessive' ? getText('excessive_moisture', language).toUpperCase() : irrigation.urgency.toUpperCase()}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#e0e0e0', mt: 0.5 }}>{irrigation.recommended_action}</Typography>
                                        </Alert>

                                        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '12px', mb: 3, border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ color: '#b0bec5' }}>Reference ET₀:</Typography>
                                                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>{irrigation.weather_snapshot.et0} mm/day</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ color: '#b0bec5' }}>{getText('crop', language)} Demand (ETc):</Typography>
                                                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>{irrigation.etc_mm_day} mm/day</Typography>
                                            </Box>
                                            <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="subtitle1" sx={{ color: '#b0bec5', fontWeight: 'bold' }}>{getText('water_need', language)}:</Typography>
                                                <Typography variant="h6" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>{irrigation.recommended_water_mm} mm</Typography>
                                            </Box>
                                        </Paper>

                                        {/* PRECISE AI RECOMMENDATION CARD */}
                                        {preciseMsg && (
                                            <Paper
                                                elevation={0}
                                                component={motion.div}
                                                initial={{ x: -20, opacity: 0 }}
                                                animate={{ x: 0, opacity: 1 }}
                                                sx={{
                                                    p: 2,
                                                    mb: 3,
                                                    background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)',
                                                    border: '1px solid rgba(0, 188, 212, 0.2)',
                                                    borderRadius: '16px',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 2
                                                }}
                                            >
                                                <Box sx={{ p: 1, bgcolor: 'rgba(0, 188, 212, 0.2)', borderRadius: '10px', color: '#00BCD4' }}>
                                                    <PsychologyIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#00BCD4', fontWeight: 'bold', letterSpacing: 1, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
                                                        {getText('precise_ai_recom', language)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.6, fontWeight: 500 }}>
                                                        "{preciseMsg}"
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        )}

                                        <Grid container spacing={1} sx={{ mt: 1 }}>
                                            <Grid item xs={4}>
                                                <Box sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <ThermostatIcon sx={{ fontSize: '1rem', color: '#ff9800', mb: 0.5 }} />
                                                    <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', fontSize: '0.6rem', fontWeight: 'bold' }}>{getText('ambient_temp', language)}</Typography>
                                                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                                        {irrigation.environment?.temp !== -999 ? `${irrigation.environment?.temp}°C` : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <OpacityIcon sx={{ fontSize: '1rem', color: '#03a9f4', mb: 0.5 }} />
                                                    <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', fontSize: '0.6rem', fontWeight: 'bold' }}>{getText('rel_humidity', language)}</Typography>
                                                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>{irrigation.environment?.humidity}%</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <AirIcon sx={{ fontSize: '1rem', color: '#9e9e9e', mb: 0.5 }} />
                                                    <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', fontSize: '0.6rem', fontWeight: 'bold' }}>{getText('wind_speed', language)}</Typography>
                                                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>{irrigation.environment?.wind_speed}<small>m/s</small></Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CloudIcon sx={{ fontSize: '1rem', color: '#00BCD4' }} />
                                                <Typography variant="caption" sx={{ color: '#b0bec5' }}>{getText('recent_rain', language)}</Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ color: (irrigation.environment?.rainfall || 0) > 0 ? '#4CAF50' : '#b0bec5', fontWeight: 'bold' }}>
                                                {irrigation.environment?.rainfall || 0} mm
                                            </Typography>
                                        </Box>

                                        {/* Growth Stage Progress */}
                                        <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: '12px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {getText('growth_progress', language)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>{irrigation.stage_progress_percent}%</Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.max(2, irrigation.stage_progress_percent)}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    bgcolor: 'rgba(255,255,255,0.05)',
                                                    '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' }
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>
                                                    {getText(irrigation.current_stage.toLowerCase().replace(' ', '_'), language)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#b0bec5' }}>
                                                    {irrigation.days_in_stage} / {irrigation.stage_duration} {getText('days', language)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Advanced Analytics: Cost & Forecast */}
                                        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0, 188, 212, 0.05)', borderRadius: '12px', border: '1px solid rgba(0, 188, 212, 0.2)' }}>
                                            <Typography variant="caption" sx={{ color: '#00BCD4', fontWeight: 'bold', mb: 2, display: 'block', textTransform: 'uppercase' }}>
                                                📊 {getText('irrigation_forecast', language)}
                                            </Typography>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CurrencyRupeeIcon sx={{ fontSize: '1.2rem', color: '#00BCD4' }} />
                                                    <Typography variant="body2" sx={{ color: '#b0bec5' }}>{getText('pumping_cost', language)}</Typography>
                                                </Box>
                                                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                                    ₹{irrigation.irrigation_cost} <small style={{ fontSize: '0.7rem', color: '#78909c' }}>({getText('per_ha', language)})</small>
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarMonthIcon sx={{ fontSize: '1.2rem', color: '#00BCD4' }} />
                                                    <Typography variant="body2" sx={{ color: '#b0bec5' }}>{getText('est_next_date', language)}</Typography>
                                                </Box>
                                                <Typography variant="body1" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>
                                                    {new Date(irrigation.next_irrigation_date).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'short' })}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Accordion sx={{
                                            backgroundColor: 'rgba(0, 188, 212, 0.05)',
                                            backgroundImage: 'none',
                                            border: '1px solid rgba(0, 188, 212, 0.2)',
                                            borderRadius: '12px !important',
                                            '&:before': { display: 'none' }
                                        }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#00BCD4' }} />}>
                                                <Typography sx={{ color: '#00BCD4', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <InfoIcon sx={{ fontSize: '1.2rem' }} /> {getText('why_recom', language)}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                {irrigation.reasoning_points.map((point, idx) => (
                                                    <Typography key={idx} variant="body2" component="div" sx={{ color: '#cfd8dc', mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                        <Box sx={{ minWidth: 6, width: 6, height: 6, bgcolor: '#00BCD4', borderRadius: '50%', mt: 0.8 }} />
                                                        {point}
                                                    </Typography>
                                                ))}
                                            </AccordionDetails>
                                        </Accordion>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* RIGHT COLUMN: Fertilizer Scheduler */}
                    <Grid item xs={12} lg={8}>
                        <Card className="premium-card">
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h5" sx={{ color: '#4CAF50', mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold' }}>
                                    <AgricultureIcon /> {getText('fert_splits', language).replace('ICAR Co 0238', farmer_info.variety)}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                                    <Chip
                                        size="small"
                                        label={language === 'hi' ? `${farmer_info.expected_yield || 100} टन उपज के लिए` : `Target: ${farmer_info.expected_yield || 100}t/ha Yield`}
                                        sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50', border: '1px solid rgba(76, 175, 80, 0.2)', fontSize: '0.7rem' }}
                                    />
                                    <Chip size="small" label="AI Optimized" sx={{ bgcolor: 'rgba(103, 58, 183, 0.1)', color: '#9575cd', border: '1px solid rgba(103, 58, 183, 0.2)', fontSize: '0.7rem' }} />
                                </Box>

                                <Stepper orientation="vertical" sx={{
                                    '& .MuiStepIcon-root.Mui-active': { color: '#4CAF50' },
                                    '& .MuiStepIcon-root.Mui-completed': { color: '#4CAF50' },
                                    '& .MuiStepLabel-label': { color: '#b0bec5' },
                                    '& .MuiStepLabel-label.Mui-active': { color: '#fff', fontWeight: 'bold' },
                                    '& .MuiStepConnector-line': { borderColor: 'rgba(76, 175, 80, 0.2)', borderLeftWidth: 2 }
                                }}>
                                    {fertilizer_schedule.map((step, index) => {
                                        const isPast = new Date(step.scheduled_date) < new Date();
                                        const isActive = !isPast && (index === 0 || new Date(fertilizer_schedule[index - 1].scheduled_date) < new Date()); // Simple logic for active
                                        const stageKey = step.stage.toLowerCase().replace(' ', '_');
                                        const translatedStage = getText(stageKey, language) !== stageKey ? getText(stageKey, language) : step.stage;

                                        return (
                                            <Step key={index} active={isActive} completed={isPast}>
                                                <StepLabel>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        bgcolor: isActive ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
                                                        borderLeft: isActive ? '3px solid #4CAF50' : 'none',
                                                        borderRadius: '4px',
                                                        p: 1,
                                                        transition: 'all 0.3s ease'
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="h6" sx={{ color: isActive ? '#fff' : (isPast ? '#4CAF50' : '#b0bec5'), fontWeight: isActive ? 'bold' : 'normal' }}>
                                                                {translatedStage} Dose
                                                            </Typography>
                                                            {isActive && (
                                                                <Chip label="ACTIVE" size="small" sx={{ height: 16, fontSize: '0.5rem', bgcolor: '#4CAF50', color: '#fff', fontWeight: 'bold' }} />
                                                            )}
                                                        </Box>
                                                        <Chip
                                                            label={step.scheduled_date}
                                                            size="small"
                                                            icon={<CalendarMonthIcon fontSize="small" />}
                                                            sx={{ bgcolor: isPast ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255,255,255,0.05)', color: isPast ? '#4CAF50' : '#b0bec5' }}
                                                        />
                                                    </Box>
                                                </StepLabel>
                                                <Box sx={{ pl: 4, pb: 4, mt: 1 }}>
                                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                                        <Grid item xs={6} sm={3}>
                                                            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                                                <Typography variant="caption" sx={{ color: '#b0bec5', fontWeight: 'bold' }}>Nitrogen (N)</Typography>
                                                                <Typography variant="h6" sx={{ color: '#fff' }}>{step.n_kg.toFixed(0)}<small style={{ fontSize: '0.6em', marginLeft: 2 }}>kg</small></Typography>
                                                            </Paper>
                                                        </Grid>
                                                        <Grid item xs={6} sm={3}>
                                                            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                                                <Typography variant="caption" sx={{ color: '#b0bec5', fontWeight: 'bold' }}>Phosphorus</Typography>
                                                                <Typography variant="h6" sx={{ color: '#fff' }}>{step.p_kg.toFixed(0)}<small style={{ fontSize: '0.6em', marginLeft: 2 }}>kg</small></Typography>
                                                            </Paper>
                                                        </Grid>
                                                        <Grid item xs={6} sm={3}>
                                                            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                                                <Typography variant="caption" sx={{ color: '#b0bec5', fontWeight: 'bold' }}>Potassium (K)</Typography>
                                                                <Typography variant="h6" sx={{ color: '#fff' }}>{step.k_kg.toFixed(0)}<small style={{ fontSize: '0.6em', marginLeft: 2 }}>kg</small></Typography>
                                                            </Paper>
                                                        </Grid>
                                                        <Grid item xs={6} sm={3}>
                                                            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)', borderRadius: '12px', boxShadow: '0 4px 15px rgba(76, 175, 80, 0.1)' }}>
                                                                <Typography variant="caption" sx={{ color: '#81C784', fontWeight: 'bold' }}>{getText('est_cost', language)}</Typography>
                                                                <Typography variant="h6" sx={{ color: '#81C784', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <CurrencyRupeeIcon sx={{ fontSize: '1rem' }} />{step.estimated_cost.toLocaleString()}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    </Grid>

                                                    <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                        <Typography variant="body2" sx={{ color: '#fff', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <InfoIcon sx={{ fontSize: '1.1rem', color: '#4CAF50' }} />
                                                            <strong>{getText('advice', language)}:</strong> {step.message}
                                                        </Typography>
                                                        <Box sx={{
                                                            mt: 1.5,
                                                            p: 1.5,
                                                            bgcolor: 'rgba(255, 171, 145, 0.08)',
                                                            borderRadius: '8px',
                                                            border: '1px solid rgba(255, 171, 145, 0.2)',
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 1.5
                                                        }}>
                                                            <WarningAmberIcon sx={{ color: '#ffab91', mt: 0.1, fontSize: '1.2rem' }} />
                                                            <Typography variant="caption" sx={{ color: '#ffab91', lineHeight: 1.4 }}>
                                                                <strong>{getText('skip_risk_warn', language)}:</strong> {step.skip_risk}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </motion.div>
        </Container >
    );
};

export default SugarcaneDashboard;
